import MarkdownContent from './MarkdownContent'
import { PermissionRequestPart } from './aiAssistantModel'
import { historyCaptionStyle } from './historyStyles'

type Props = {
  part: PermissionRequestPart
  interactive: boolean
  onDecide: (toolCallId: string, approved: boolean) => void
}

const buttonStyle = (interactive: boolean, primary: boolean) => ({
  padding: '6px 16px',
  borderRadius: 8,
  border: primary ? '1px solid #1677ff' : '1px solid #d9d9d9',
  background: primary ? '#1677ff' : '#fff',
  color: primary ? '#fff' : '#333',
  cursor: interactive ? 'pointer' : 'default',
  opacity: interactive ? 1 : 0.6,
  font: 'inherit',
})

// The proposal-descriptions skill drafts 60-150 words with a bulleted
// include-list, so the block is capped and scrolls rather than pushing the
// Approve button out of reach.
const bodyStyle = {
  margin: '0 0 12px',
  padding: '8px 10px',
  border: '1px solid #f0e0a0',
  borderRadius: 6,
  background: '#fff',
  maxHeight: 240,
  overflowY: 'auto' as const,
}

const badgeStyle = (approved: boolean) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 12,
  border: approved ? '1px solid #1677ff' : '1px solid #d9d9d9',
  background: approved ? '#1677ff' : '#fff',
  color: approved ? '#fff' : '#666',
  fontSize: 12,
})

const WriteConfirm = ({ part, interactive, onDecide }: Props) => {
  const description = part.summary?.trim() || 'The assistant wants to make a change.'
  // A decided write comes from history: show the outcome, not dead controls.
  const decided = part.status === 'approved' || part.status === 'denied'

  return (
    <div
      style={{
        margin: '8px 0',
        border: '1px solid #ffe58f',
        background: '#fffbe6',
        borderRadius: 8,
        padding: '12px',
      }}
    >
      <p style={{ margin: '0 0 4px', fontWeight: 500 }}>Approve this change?</p>
      <p style={{ margin: part.body ? '0 0 8px' : '0 0 12px' }}>{description}</p>
      {part.body && (
        <div style={bodyStyle} data-testid="write-confirm-body">
          <MarkdownContent content={part.body} />
        </div>
      )}
      {decided ? (
        <span style={badgeStyle(part.status === 'approved')}>
          {part.status === 'approved' ? 'Approved' : 'Denied'}
        </span>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              disabled={!interactive}
              onClick={() => onDecide(part.tool_call_id, true)}
              style={buttonStyle(interactive, true)}
            >
              Approve
            </button>
            <button
              type="button"
              disabled={!interactive}
              onClick={() => onDecide(part.tool_call_id, false)}
              style={buttonStyle(interactive, false)}
            >
              Deny
            </button>
          </div>
          {/* Historical and still undecided: the user walked away from it. */}
          {part.status === 'pending' && (
            <p style={historyCaptionStyle}>No decision was made.</p>
          )}
        </>
      )}
    </div>
  )
}

export default WriteConfirm
